import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Henter ref fra URL (eks: saleswork.no/signup?ref=selger_123)
  const refCodeFromUrl = searchParams.get('ref') || '';

  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [userCount, setUserCount] = useState(1);
  const [step, setStep] = useState(1);
  
  // Nye tilstander for betaling og sporing
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' eller 'invoice'
  const [discountCode, setDiscountCode] = useState('');
  const [referralCode, setReferralCode] = useState(refCodeFromUrl);
  const [invoiceDetails, setInvoiceDetails] = useState({ orgNr: '', email: '', reference: '' });

  const plans = {
      start: { name: 'Start', price: 599 },
      pro: { name: 'Vekst', price: 990 },
      enterprise: { name: 'Enterprise', price: 2990 }
  };

  const handleRegister = (e) => {
    e.preventDefault();
    setStep(2);
    
    // Simulering av API-kall med sporing og betalingsinfo
    console.log("Registrerer konto:", {
        plan: selectedPlan,
        users: userCount,
        payment: paymentMethod,
        invoiceInfo: paymentMethod === 'invoice' ? invoiceDetails : 'Stripe Token',
        discount: discountCode,
        referredBy: referralCode // Her kobles salget til partner/selger
    });

    setTimeout(() => {
        alert("Konto opprettet! Du blir nå videresendt til innlogging.");
        navigate('/login');
    }, 2000);
  };

  const totalPrice = plans[selectedPlan].price * userCount;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-12 px-4">
      <Link to="/" className="mb-8 flex items-center gap-2 text-2xl font-black text-slate-900 hover:text-blue-600 transition-colors">
        <SafeIcon icon="⬅️" className="text-lg" /> Tilbake til forside
      </Link>

      <div className="bg-white max-w-6xl w-full rounded-[2.5rem] shadow-xl border border-slate-200 overflow-hidden flex flex-col md:flex-row">
        {/* Left: Plan & Summary */}
        <div className="md:w-1/3 bg-slate-900 text-white p-8 md:p-10 flex flex-col">
          <h2 className="text-2xl font-black mb-6">Din Ordre</h2>
          <div className="space-y-4">
            {Object.entries(plans).map(([key, plan]) => (
                <div 
                  key={key}
                  onClick={() => setSelectedPlan(key)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedPlan === key ? 'border-blue-500 bg-white/10' : 'border-white/10 hover:bg-white/5'}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold">{plan.name}</span>
                    <span className="font-mono">{plan.price},-</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest">Pr bruker / mnd</p>
                </div>
            ))}
          </div>

          <div className="mt-8 pt-8 border-t border-white/10">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Antall Brukere</label>
              <div className="flex items-center gap-4 bg-white/5 p-2 rounded-xl border border-white/10 w-fit">
                  <button onClick={() => setUserCount(Math.max(1, userCount - 1))} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center font-black">-</button>
                  <span className="font-black text-lg w-8 text-center">{userCount}</span>
                  <button onClick={() => setUserCount(userCount + 1)} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center font-black">+</button>
              </div>
          </div>

          <div className="mt-auto pt-8">
              <div className="flex justify-between items-end mb-4">
                  <span className="text-sm font-bold text-slate-400">Totalpris eks. mva:</span>
                  <span className="text-3xl font-black">{totalPrice},- <span className="text-sm font-normal text-slate-400">/mnd</span></span>
              </div>
            {referralCode && (
                <div className="mb-4 p-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg text-xs text-emerald-400 font-bold flex items-center gap-2">
                    <SafeIcon icon="🤝" /> Registreres på partner: {referralCode}
                </div>
            )}
            <div className="flex items-center gap-2 text-sm text-emerald-400">
              <SafeIcon icon="✅" /> 14 dagers gratis prøveperiode
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <div className="md:w-2/3 p-8 md:p-12 bg-white">
          <h2 className="text-3xl font-black text-slate-900 mb-8">Opprett konto</h2>
          
          {step === 1 ? (
            <form onSubmit={handleRegister} className="space-y-8">
                {/* Contact Info */}
                <div className="space-y-4">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">1. Kontaktinfo</h3>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Fornavn</label>
                            <input required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-bold" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Etternavn</label>
                            <input required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-bold" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">E-post (Jobb)</label>
                            <input required type="email" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-bold" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Telefon</label>
                            <input required type="tel" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-bold" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Passord</label>
                        <input required type="password" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-bold" />
                    </div>
                </div>

                {/* Payment & Codes */}
                <div className="space-y-4">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">2. Betaling & Referanser</h3>
                    
                    {/* Discount & Referral */}
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Rabattkode</label>
                            <input 
                                value={discountCode}
                                onChange={(e) => setDiscountCode(e.target.value)}
                                placeholder="VALGFRI"
                                className="w-full p-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm" 
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Partner / Selger ID</label>
                            <input 
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value)}
                                placeholder="VALGFRI"
                                className={`w-full p-3 border rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${referralCode ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white border-slate-200'}`}
                            />
                        </div>
                    </div>

                    {/* Payment Method Toggle */}
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Betalingsmetode</label>
                        <div className="flex gap-4 mb-4">
                            <button
                                type="button" 
                                onClick={() => setPaymentMethod('card')}
                                className={`flex-1 py-3 px-4 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition-all ${paymentMethod === 'card' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                            >
                                <SafeIcon icon="💳" /> Kort (Stripe)
                            </button>
                            <button 
                                type="button"
                                onClick={() => setPaymentMethod('invoice')}
                                className={`flex-1 py-3 px-4 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition-all ${paymentMethod === 'invoice' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                            >
                                <SafeIcon icon="📄" /> Faktura (EHF/PDF)
                            </button>
                        </div>

                        {/* Conditional Payment Fields */}
                        {paymentMethod === 'card' ? (
                            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-bold text-slate-500 uppercase">Sikker Betaling</span>
                                    <div className="flex gap-1 opacity-50">
                                        <div className="w-8 h-5 bg-slate-300 rounded"></div>
                                        <div className="w-8 h-5 bg-slate-300 rounded"></div>
                                    </div>
                                </div>
                                <input placeholder="Kortnummer" className="w-full p-3 mb-2 bg-white border border-slate-200 rounded-lg text-sm outline-none" />
                                <div className="grid grid-cols-2 gap-2">
                                    <input placeholder="MM/ÅÅ" className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm outline-none" />
                                    <input placeholder="CVC" className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm outline-none" />
                                </div>
                                <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1"><SafeIcon icon="🔒" /> Behandles kryptert via Stripe.</p>
                            </div>
                        ) : (
                            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Organisasjonsnummer</label>
                                    <input 
                                        required 
                                        placeholder="9 sifre"
                                        value={invoiceDetails.orgNr} 
                                        onChange={e => setInvoiceDetails({...invoiceDetails, orgNr: e.target.value})}
                                        className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm outline-none" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Faktura E-post (EHF auto hvis tilgjengelig)</label>
                                    <input 
                                        required 
                                        type="email"
                                        value={invoiceDetails.email} 
                                        onChange={e => setInvoiceDetails({...invoiceDetails, email: e.target.value})}
                                        className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm outline-none" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Merk Faktura (Ref)</label>
                                    <input 
                                        value={invoiceDetails.reference} 
                                        onChange={e => setInvoiceDetails({...invoiceDetails, reference: e.target.value})}
                                        className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm outline-none" 
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="flex items-center gap-3 py-4 border-t border-slate-100">
                    <input type="checkbox" required className="w-5 h-5 rounded text-blue-600" />
                    <span className="text-sm text-slate-600">Jeg godtar <Link to="/terms" target="_blank" className="text-blue-600 underline">vilkårene</Link> og <Link to="/privacy" target="_blank" className="text-blue-600 underline">personvernerklæringen</Link>.</span>
                </div>

                <button type="submit" className="w-full py-4 bg-blue-600 text-white rounded-xl font-black text-lg uppercase tracking-widest hover:bg-blue-700 shadow-lg transition-all">
                    Fullfør Bestilling
                </button>
            </form>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <h3 className="text-xl font-bold">Oppretter ditt miljø...</h3>
                <p className="text-slate-500 mb-4">Registrerer lisenser og setter opp database.</p>
                {referralCode && <p className="text-emerald-600 font-bold text-sm">Validert partner: {referralCode}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;